(() => {
  const rootSelector = '[data-insole-comparison-carousel]';

  class InsoleComparisonCarousel {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-insole-carousel-stage]');
      this.slides = Array.from(root.querySelectorAll('[data-insole-carousel-slide]'));
      this.tabsTrack = root.querySelector('[data-insole-carousel-tabs-track]');
      this.tabs = Array.from(root.querySelectorAll('[data-insole-carousel-tab]'));
      this.descriptions = Array.from(root.querySelectorAll('[data-insole-carousel-description]'));
      this.previousButton = root.querySelector('[data-insole-carousel-previous]');
      this.nextButton = root.querySelector('[data-insole-carousel-next]');
      this.status = root.querySelector('[data-insole-carousel-status]');
      this.currentIndex = Math.min(Number(root.dataset.initialSlide) || 0, Math.max(this.slides.length - 1, 0));
      this.pointerStartX = null;
      this.resizeFrame = null;

      this.previousButton?.addEventListener('click', () => this.show(this.currentIndex - 1, true));
      this.nextButton?.addEventListener('click', () => this.show(this.currentIndex + 1, true));
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.show(Number(tab.dataset.insoleCarouselTab), true));
      });
      this.stage?.addEventListener('keydown', (event) => this.onKeydown(event));
      this.stage?.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.stage?.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.stage?.addEventListener('pointercancel', () => {
        this.pointerStartX = null;
      });

      this.onResize = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => {
          this.prepareMarquee(this.slides[this.currentIndex], true);
          this.arrangeTabs();
        });
      };

      window.addEventListener('resize', this.onResize, { passive: true });
      this.show(this.currentIndex, false);
    }

    show(requestedIndex, announce) {
      if (!this.slides.length) return;

      this.currentIndex = (requestedIndex + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, index) => {
        const active = index === this.currentIndex;
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });

      this.tabs.forEach((tab, index) => {
        const active = index === this.currentIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.descriptions.forEach((description, index) => {
        const active = index === this.currentIndex;
        description.hidden = !active;
        description.setAttribute('aria-hidden', String(!active));
      });

      const currentSlide = this.slides[this.currentIndex];
      this.prepareMarquee(currentSlide);
      this.arrangeTabs();

      if (announce && this.status) {
        const productName = currentSlide.dataset.productName || `Product ${this.currentIndex + 1}`;
        this.status.textContent = `${productName}, ${this.currentIndex + 1} of ${this.slides.length}`;
      }

      const hideControls = this.slides.length < 2;
      if (this.previousButton) this.previousButton.hidden = hideControls;
      if (this.nextButton) this.nextButton.hidden = hideControls;
    }

    arrangeTabs() {
      if (!this.tabsTrack || this.tabs.length < 2) return;

      const middle = Math.floor(this.tabs.length / 2);
      const firstIndex = this.currentIndex - middle;

      for (let offset = 0; offset < this.tabs.length; offset += 1) {
        const index = (firstIndex + offset + this.tabs.length) % this.tabs.length;
        this.tabsTrack.append(this.tabs[index]);
      }

      const activeTab = this.tabs[this.currentIndex];
      const activeCenter = activeTab.offsetLeft + activeTab.offsetWidth / 2;
      this.tabsTrack.style.setProperty('--insole-tabs-shift', `${-activeCenter}px`);
    }

    prepareMarquee(slide, rebuild = false) {
      const marquee = slide?.querySelector('[data-best-for-marquee]');
      const track = marquee?.querySelector('[data-best-for-track]');
      const group = track?.querySelector('[data-best-for-group]');

      if (!marquee || !track || !group || !group.children.length) return;
      if (track.dataset.ready === 'true' && !rebuild) return;

      track.classList.remove('is-ready');
      track.querySelectorAll('[data-marquee-copy]').forEach((item) => item.remove());
      group.querySelectorAll('[data-marquee-fill]').forEach((item) => item.remove());

      const sourceItems = Array.from(group.querySelectorAll('[data-best-for-item]'));
      let safety = 0;

      while (group.scrollWidth < marquee.clientWidth && safety < 20) {
        sourceItems.forEach((item) => {
          const fillItem = item.cloneNode(true);
          fillItem.removeAttribute('id');
          fillItem.removeAttribute('data-shopify-editor-block');
          fillItem.setAttribute('aria-hidden', 'true');
          fillItem.dataset.marqueeFill = '';
          group.append(fillItem);
        });
        safety += 1;
      }

      const copy = group.cloneNode(true);
      copy.removeAttribute('data-best-for-group');
      copy.dataset.marqueeCopy = '';
      copy.setAttribute('aria-hidden', 'true');
      track.append(copy);

      const trackGap = Number.parseFloat(getComputedStyle(track).columnGap) || 9;
      const duration = Number.parseFloat(this.root.dataset.marqueeDuration) || 18;
      track.style.setProperty('--insole-marquee-distance', `${group.getBoundingClientRect().width + trackGap}px`);
      track.style.setProperty('--insole-marquee-duration', `${duration}s`);
      track.dataset.ready = 'true';

      requestAnimationFrame(() => track.classList.add('is-ready'));
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.show(this.currentIndex - 1, true);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.currentIndex + 1, true);
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' || event.target.closest('button, a, input, select, textarea')) return;
      this.pointerStartX = event.clientX;
    }

    onPointerUp(event) {
      if (this.pointerStartX === null) return;

      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;

      if (Math.abs(distance) < 48) return;
      this.show(this.currentIndex + (distance < 0 ? 1 : -1), true);
    }
  }

  const initializeCarousels = (scope = document) => {
    const roots = [];

    if (scope.matches?.(rootSelector)) roots.push(scope);
    roots.push(...scope.querySelectorAll(rootSelector));

    roots.forEach((root) => {
      if (!root.insoleComparisonCarousel) {
        root.insoleComparisonCarousel = new InsoleComparisonCarousel(root);
      }
    });
  };

  initializeCarousels();

  if (!window.insoleComparisonCarouselEventsBound) {
    document.addEventListener('shopify:section:load', (event) => initializeCarousels(event.target));
    document.addEventListener('shopify:block:select', (event) => {
      const root = event.target.closest(rootSelector);
      const slide = event.target.closest('[data-insole-carousel-slide]');

      if (!root?.insoleComparisonCarousel || !slide) return;
      root.insoleComparisonCarousel.show(Number(slide.dataset.slideIndex), false);
    });
    window.insoleComparisonCarouselEventsBound = true;
  }
})();
